/**
 * The person's Entra token for Sumi, as the task pane holds it (BON-1649).
 *
 * ── TWO WAYS TO GET ONE ─────────────────────────────────────────────────────────────────────────
 *
 * 1. Nested app authentication (NAA), Microsoft's current way for an Office add-in to sign in
 *    (learn.microsoft.com, "Enable single sign-on in an Office add-in with nested app
 *    authentication"). MSAL's nestable client asks Word, which already knows who is signed in, to
 *    broker a token for the add-in's own scope (`access_as_user`). Silently first; when Entra needs
 *    the person (consent, multi-factor, an expired session) MSAL says interaction is required and a
 *    popup asks them.
 * 2. MSAL in an Office dialog (`dialog.ts`), where Word does not offer NAA: a host without the
 *    NestedAppAuth 1.1 requirement set (an older Word; Word on the web for a document not opened from
 *    SharePoint or OneDrive), or where NAA fails for a reason a popup cannot fix.
 *
 * Both tokens are issued to the add-in's own registration for its own scope, so Sumi checks one
 * client id (apps/platform/lib/wordAddinAuth.ts). Office's legacy single sign-on
 * (`Office.auth.getAccessToken`), which this replaced on 14 October 2026, is gone.
 *
 * ── HOLDING IT ──────────────────────────────────────────────────────────────────────────────────
 *
 * The keeper holds the token in memory and renews it five minutes before it expires. When Sumi
 * refuses one (`refresh`), it asks again with a forced refresh: MSAL skips its cache and Entra issues
 * a new token, without the person seeing anything unless Entra needs them.
 */
import { AcademyError } from '@academy/core/errors';

export type SignInProblem = 'cancelled' | 'failed';

export class SignInError extends AcademyError {
  constructor(
    readonly problem: SignInProblem,
    message: string,
    metadata?: Record<string, unknown>,
  ) {
    super(problem === 'cancelled' ? 'SIGN_IN_CANCELLED' : 'SIGN_IN_FAILED', message, metadata);
    this.name = 'SignInError';
  }
}

/** Nested app authentication, as `naa.ts` makes it from MSAL. */
export interface NestedSignIn {
  /**
   * A token without asking the person: MSAL's cache, or the broker. `forceRefresh` skips the cache.
   * Resolves null when Entra needs the person (MSAL's InteractionRequiredAuthError); rejects for
   * anything a popup cannot fix.
   */
  silent(forceRefresh: boolean): Promise<string | null>;
  /** MSAL's popup. Rejects with a `SignInError`: cancelled when the person closed it. */
  interactive(): Promise<string>;
}

export interface TokenSources {
  /** Null where Word does not support NestedAppAuth 1.1. */
  nested: NestedSignIn | null;
  /** Sign-in in an Office dialog. Rejects with a `SignInError`. */
  dialog(): Promise<string>;
}

/** Renewed this long before it expires. */
export const RENEW_BEFORE_MS = 5 * 60 * 1000;

/** The token's `exp`, in milliseconds, read without verifying (Sumi verifies; this only schedules). */
export function expiresAt(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: unknown;
    };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

export interface TokenKeeper {
  /** A token that is not about to expire, signing in if need be. */
  get(): Promise<string>;
  /** Sumi refused the one held: get a new one, bypassing every cache. */
  refresh(): Promise<string>;
  /** Which way the current token came, for support. */
  readonly via: 'nested' | 'dialog' | null;
}

export function createTokenKeeper(
  sources: TokenSources,
  now: () => number = Date.now,
): TokenKeeper {
  let held: { token: string; until: number | null } | null = null;
  let via: 'nested' | 'dialog' | null = null;
  /* Once NAA has failed for a reason a popup cannot fix, later tokens come from the dialog. */
  let nestedFailed = false;
  /* One sign-in at a time: two buttons pressed together must not open two popups. */
  let inFlight: Promise<string> | null = null;

  const fresh = () =>
    held !== null && (held.until === null || held.until - RENEW_BEFORE_MS > now());

  async function obtain(forceRefresh: boolean): Promise<string> {
    if (sources.nested && !nestedFailed) {
      let token: string | null = null;
      try {
        token = await sources.nested.silent(forceRefresh);
      } catch {
        nestedFailed = true;
      }
      if (!nestedFailed) {
        /* A cancelled or failed popup is the person's answer, reported as it is: no dialog after. */
        token ??= await sources.nested.interactive();
        via = 'nested';
        return token;
      }
    }
    const token = await sources.dialog();
    via = 'dialog';
    return token;
  }

  function start(forceRefresh: boolean): Promise<string> {
    inFlight ??= obtain(forceRefresh)
      .then((token) => {
        held = { token, until: expiresAt(token) };
        return token;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  }

  return {
    async get() {
      if (fresh()) return held!.token;
      return start(false);
    },
    async refresh() {
      held = null;
      return start(true);
    },
    get via() {
      return via;
    },
  };
}
